/**
 * データ復旧ユーティリティ
 * LocalStorageとIndexedDBの両方からデータを検査・復旧
 */

export async function investigateDataStatus() {
  console.group('📊 データ状態の調査')
  
  // 1. LocalStorageの全データを確認
  console.group('📦 LocalStorage内容')
  const localStorageData: Record<string, any> = {}
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key) {
      try {
        const value = localStorage.getItem(key)
        if (value) {
          // パースを試みる
          try {
            localStorageData[key] = JSON.parse(value)
            console.log(`✅ ${key}: ${value.length} bytes (JSON)`)
          } catch {
            localStorageData[key] = value
            console.log(`📝 ${key}: ${value.length} bytes (Text)`)
          }
        }
      } catch (error) {
        console.error(`❌ ${key}: 読み取りエラー`, error)
      }
    }
  }
  console.log('LocalStorage合計キー数:', Object.keys(localStorageData).length)
  console.groupEnd()
  
  // 2. IndexedDBの全データベースを確認
  console.group('🗄️ IndexedDB内容')
  try {
    const databases = await indexedDB.databases()
    console.log('データベース一覧:', databases)
    
    // CreaNizerDBを詳しく調査
    const dbName = 'CreaNizerDB'
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(dbName, 4)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
    
    console.log('データベースバージョン:', db.version)
    console.log('オブジェクトストア一覧:', Array.from(db.objectStoreNames))
    
    // 各ストアのデータを確認
    const storeData: Record<string, any[]> = {}
    for (const storeName of db.objectStoreNames) {
      const transaction = db.transaction([storeName], 'readonly')
      const store = transaction.objectStore(storeName)
      const data = await new Promise<any[]>((resolve, reject) => {
        const request = store.getAll()
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
      })
      
      storeData[storeName] = data
      console.log(`📊 ${storeName}: ${data.length}件のレコード`)
      
      // zustandStoresの内容を詳しく表示
      if (storeName === 'zustandStores') {
        console.group('Zustandストア詳細:')
        data.forEach(item => {
          console.log(`  - ${item.key}:`, item.value ? '有効' : '空', 
            item.migratedAt ? `(移行日: ${item.migratedAt})` : '')
        })
        console.groupEnd()
      }
    }
    
    db.close()
    console.groupEnd()
    
    return { localStorageData, indexedDBData: storeData }
  } catch (error) {
    console.error('IndexedDB調査エラー:', error)
    console.groupEnd()
    return { localStorageData, indexedDBData: null }
  }
}

export async function findBackups() {
  console.group('🔍 バックアップ検索')
  
  const backups: Record<string, any> = {}
  
  // LocalStorageからバックアップを探す
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && (key.includes('backup') || key.includes('fallback'))) {
      const value = localStorage.getItem(key)
      if (value) {
        try {
          backups[key] = JSON.parse(value)
          console.log(`✅ バックアップ発見: ${key}`)
        } catch {
          backups[key] = value
          console.log(`📝 バックアップ発見(テキスト): ${key}`)
        }
      }
    }
  }
  
  console.groupEnd()
  return backups
}

export async function recoverFromBackups() {
  console.group('♻️ バックアップからの復旧試行')
  
  const backups = await findBackups()
  const recovered: Record<string, boolean> = {}
  
  for (const [backupKey, backupData] of Object.entries(backups)) {
    // バックアップキーから元のキーを推測
    let originalKey = backupKey
      .replace(/_backup_\d+/, '')
      .replace(/_fallback/, '')
    
    // 元のキーにデータがなければ復旧
    if (!localStorage.getItem(originalKey)) {
      try {
        const dataToRestore = typeof backupData === 'string' 
          ? backupData 
          : JSON.stringify(backupData)
        
        localStorage.setItem(originalKey, dataToRestore)
        recovered[originalKey] = true
        console.log(`✅ ${originalKey} を復旧しました`)
      } catch (error) {
        recovered[originalKey] = false
        console.error(`❌ ${originalKey} の復旧に失敗:`, error)
      }
    } else {
      console.log(`⏭️ ${originalKey} は既存データあり（スキップ）`)
    }
  }
  
  console.groupEnd()
  return recovered
}

export async function showStoreSummary() {
  console.group('📈 ストア別データサマリー')
  
  const stores = [
    'character-store',
    'plot-store',
    'name-store',
    'relationship-store',
    'worldbuilder-storage',
    'enemy-store',
    'title-store',
    'creanizer-wikipedias',
    'item-storage',
    'settings-storage',
    'imagePrompt-store'
  ]
  
  for (const storeName of stores) {
    const localData = localStorage.getItem(storeName)
    
    if (localData) {
      try {
        const parsed = JSON.parse(localData)
        const state = parsed.state || parsed
        
        console.group(`📦 ${storeName}`)
        
        // データ数を数える
        if (state.savedCharacters) console.log(`  キャラクター: ${state.savedCharacters.length}件`)
        if (state.savedPlots) console.log(`  プロット: ${state.savedPlots.length}件`)
        if (state.savedNames) console.log(`  名前: ${state.savedNames.length}件`)
        if (state.relationships) console.log(`  関係性: ${state.relationships.length}件`)
        if (state.savedWorlds) console.log(`  世界観: ${state.savedWorlds.length}件`)
        if (state.savedEnemies) console.log(`  エネミー: ${state.savedEnemies.length}件`)
        if (state.savedTitles) console.log(`  タイトル: ${state.savedTitles.length}件`)
        if (state.savedWikipedias) console.log(`  架空事典: ${state.savedWikipedias.length}件`)
        if (state.items) console.log(`  アイテム: ${state.items.length}件`)
        if (state.savedPrompts) console.log(`  画像プロンプト: ${state.savedPrompts.length}件`)
        if (state.apiKeys) console.log(`  APIキー: ${state.apiKeys.length}件`)
        
        console.log(`  データサイズ: ${localData.length} bytes`)
        console.groupEnd()
      } catch (error) {
        console.log(`❌ ${storeName}: パースエラー`)
      }
    } else {
      console.log(`❌ ${storeName}: データなし`)
    }
  }
  
  console.groupEnd()
}

// 実行関数
export async function fullDataRecovery() {
  console.clear()
  console.log('🚀 データ復旧プロセス開始...')
  console.log('=' .repeat(50))
  
  // 1. 現状調査
  const status = await investigateDataStatus()
  
  // 2. バックアップ検索
  const backups = await findBackups()
  
  // 3. ストアサマリー表示
  await showStoreSummary()
  
  // 4. 復旧提案
  console.group('💡 復旧提案')
  
  if (Object.keys(backups).length > 0) {
    console.log('バックアップが見つかりました。復旧を試みますか？')
    console.log('実行: await recoverFromBackups()')
  }
  
  // LocalStorageにデータがある場合
  const hasLocalData = Object.keys(status.localStorageData).some(key => 
    key.includes('store') || key.includes('storage')
  )
  
  if (hasLocalData) {
    console.log('LocalStorageにストアデータが残っています。')
    console.log('これらは現在も使用可能です。')
  }
  
  console.groupEnd()
  
  return {
    status,
    backups,
    hasLocalData
  }
}